import { Link } from "react-router-dom";
import { ArrowUpRight, Instagram, Mail } from "lucide-react";
import NewsletterForm from "@/components/NewsletterForm";
import DonateButton from "@/components/DonateButton";
import { NAV_LINKS, INSTAGRAM_URL, CONTACT_EMAIL, WELLNESS_URL, ZEFFY_URL } from "@/lib/site";

export default function Footer() {
  return (
    <footer className="relative bg-forest text-ivory" data-testid="site-footer">
      <div className="grain absolute inset-0" />
      <div className="relative mx-auto max-w-7xl px-5 pb-28 pt-20 lg:px-8 lg:pb-12">
        <div className="grid gap-14 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-4" data-testid="footer-brand">
              <img src="/brand/nseya-logo.png" alt="Nseya Foundation logo" className="h-16 w-16 rounded-full bg-white object-contain p-1.5" />
              <div>
                <p className="font-display text-2xl font-semibold">NSEYA FOUNDATION</p>
                <p className="eyebrow mt-1 text-gold">Change for Congo</p>
              </div>
            </div>
            <h3 className="mt-10 font-display text-2xl italic text-ivory sm:text-3xl" data-testid="footer-newsletter-heading">
              Stay connected to the movement.
            </h3>
            <p className="mt-3 max-w-md text-sm leading-relaxed text-ivory/70">
              Follow the journey from the first well to the vision of a sustainable sanctuary.
            </p>
            <div className="mt-6 max-w-lg">
              <NewsletterForm dark idPrefix="footer-newsletter" />
            </div>
          </div>

          <div className="lg:col-span-3">
            <p className="eyebrow mb-6 text-ivory/50">Explore</p>
            <ul className="space-y-3">
              {NAV_LINKS.map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="text-sm text-ivory/80 transition-colors hover:text-gold" data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}>
                    {l.label}
                  </Link>
                </li>
              ))}
              <li>
                <a href={ZEFFY_URL} target="_blank" rel="noopener noreferrer" className="text-sm font-semibold text-gold transition-colors hover:text-ivory" data-testid="footer-link-donate">
                  Donate
                </a>
              </li>
            </ul>
          </div>

          <div className="lg:col-span-4">
            <p className="eyebrow mb-6 text-ivory/50">Connect</p>
            <ul className="space-y-4 text-sm text-ivory/80">
              <li>
                <a href={`mailto:${CONTACT_EMAIL}`} className="inline-flex items-center gap-2 transition-colors hover:text-gold" data-testid="footer-email-link">
                  <Mail className="h-4 w-4 text-gold" /> {CONTACT_EMAIL}
                </a>
              </li>
              <li>
                <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 transition-colors hover:text-gold" data-testid="footer-instagram-link">
                  <Instagram className="h-4 w-4 text-gold" /> @changeforcongo
                </a>
              </li>
              <li>
                <a href={WELLNESS_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 transition-colors hover:text-gold" data-testid="footer-wellness-link">
                  Nseya Wellness Retreats <ArrowUpRight className="h-4 w-4" />
                </a>
              </li>
            </ul>
            <div className="mt-8">
              <DonateButton variant="gold" testid="footer-donate-button" />
            </div>
            <p className="mt-8 font-display text-sm italic text-ivory/60">Safety. Dignity. Clean Water. Community.</p>
          </div>
        </div>

        <div className="mt-16 flex flex-col items-start justify-between gap-3 border-t border-ivory/15 pt-6 text-xs text-ivory/50 sm:flex-row sm:items-center">
          <p data-testid="footer-copyright">© {new Date().getFullYear()} Nseya Foundation · Fondation Humanitaire</p>
          <p data-testid="footer-legal-note">Privacy Policy &amp; Terms available upon request.</p>
        </div>
      </div>
    </footer>
  );
}
